import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";

import { useBrainFetcher } from "@/app/studio/[brainId]/ManagementTabs/hooks/useKnowledgeBaseFetcher";
import { Icon } from "@/lib/components/ui/Icon/Icon";
import { LoaderIcon } from "@/lib/components/ui/LoaderIcon/LoaderIcon";
import { useNotificationsContext } from "@/lib/context/NotificationsProvider/hooks/useNotificationsContext";
import { useAxios } from "@/lib/hooks";

import styles from "./NotificationCard.module.scss";

import { BulkNotification, NotificationStatus } from "../../../types/types";
import { NotificationProgressBar } from "../NotificationProgressBar/NotificationProgressBar";

interface NotificationCardProps {
  bulkNotification: BulkNotification;
}

const getCategoryLabelKey = (
  category: BulkNotification["category"],
  isCompleted: boolean,
  hasError: boolean,
): string | null => {
  const keys: Record<string, { active: string; done: string; error: string }> =
    {
      upload: {
        active: "notificationUploading",
        done: "notificationUploaded",
        error: "notificationFailedToUpload",
      },
      crawl: {
        active: "notificationCrawling",
        done: "notificationCrawled",
        error: "notificationFailedToCrawl",
      },
      sync: {
        active: "notificationSyncing",
        done: "notificationSynced",
        error: "notificationFailedToSync",
      },
    };
  const pair = keys[category];
  if (!pair) {
    return null;
  }

  if (isCompleted && hasError) {
    return pair.error;
  }

  return isCompleted ? pair.done : pair.active;
};

export const NotificationCard = ({
  bulkNotification,
}: NotificationCardProps): JSX.Element => {
  const { brain } = useBrainFetcher({ brainId: bulkNotification.brain_id });
  const { axiosInstance } = useAxios();
  const { updateNotifications } = useNotificationsContext();
  const { t } = useTranslation("translation");

  const total = bulkNotification.notifications.length;
  const completed = bulkNotification.notifications.filter(
    (n) => n.status !== NotificationStatus.Info,
  ).length;
  const allCompleted = completed === total;
  const hasError = bulkNotification.notifications.some(
    (n) => n.status === NotificationStatus.Error,
  );

  const timeAgo =
    formatDistanceToNow(new Date(bulkNotification.datetime), {
      addSuffix: false,
    }).replace("about ", "") + " ago";

  const deleteNotification = async () => {
    try {
      await Promise.all(
        bulkNotification.notifications.map((n) =>
          axiosInstance.delete(`/notifications/${n.id}`),
        ),
      );
      await updateNotifications();
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const brainName = brain?.name ?? "";
  const labelKey = getCategoryLabelKey(
    bulkNotification.category,
    allCompleted,
    hasError,
  );
  const categoryLabel = labelKey
    ? t("notificationFilesFor", { label: t(labelKey as any) })
    : "";

  if (allCompleted) {
    return (
      <div className={styles.card_completed}>
        <div className={styles.content}>
          <div className={styles.title_container}>
            <span className={styles.title}>
              {categoryLabel}
              <span className={styles.brain_name}>{brainName}</span>
            </span>
            <span className={styles.check_icon}>
              {hasError ? (
                <Icon name="warning" size="small" color="warning" />
              ) : (
                <Icon name="check" size="small" color="success" />
              )}
            </span>
            <span className={styles.count}>
              {completed}/{total}
            </span>
          </div>

          <span className={styles.timestamp}>{timeAgo}</span>
          <span
            className={styles.delete_button}
            onClick={() => void deleteNotification()}
          >
            <Icon name="delete" size="small" color="black" />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>
          {categoryLabel}
          <span className={styles.brain_name}>{brainName}</span>
        </span>
        <span
          className={styles.close_button}
          onClick={() => void deleteNotification()}
        >
          <Icon name="close" size="small" color="black" />
        </span>
      </div>
      <div className={styles.progress_row}>
        <div className={styles.progress_info}>
          <LoaderIcon size="normal" color="grey" />
          <span className={styles.count}>
            {completed}/{total}
          </span>
          <NotificationProgressBar bulkNotification={bulkNotification} />
        </div>
        <span className={styles.timestamp}>{timeAgo}</span>
      </div>
    </div>
  );
};
